import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { lessonsAPI, progressAPI, weakTopicsAPI, gamificationAPI, topicsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import StudentHeader from '@/components/StudentHeader';
import MaterialsBar from '@/components/MaterialsBar';
import { ContentBlockRenderer, QuizView, QuizResults, LessonSummaryCards } from '@/components/lesson';
import {
    BookOpen, Clock, CheckCircle, Scale, ChevronRight, ChevronLeft, ArrowLeft,
    AlertTriangle, CheckCircle2, Play, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LEVEL_COLORS = {
    L1: 'from-green-500 to-green-600',
    L2: 'from-blue-500 to-blue-600',
    L3: 'from-purple-500 to-purple-600',
    L4: 'from-amber-500 to-amber-600'
};

// FlipCard component
const FlipCard = ({ front, back, ref_text }) => {
    const [flipped, setFlipped] = React.useState(false);
    return (
        <div className="cursor-pointer" onClick={() => setFlipped(!flipped)}
            style={{ perspective: '1000px' }}>
            <div className="relative h-[220px] transition-all duration-500"
                style={{ transformStyle: 'preserve-3d', transform: flipped ? 'rotateY(180deg)' : 'none' }}>
                <div className="absolute inset-0 p-5 rounded-xl border-2 border-amber-500/20 bg-gradient-to-br from-amber-500/[0.04] to-transparent flex flex-col items-center justify-center text-center"
                    style={{ backfaceVisibility: 'hidden' }}>
                    <div className="text-[9px] text-amber-400/50 font-bold tracking-widest mb-3">SORU</div>
                    <p className="text-sm text-foreground/80 leading-relaxed px-2">{front}</p>
                </div>
                <div className="absolute inset-0 p-5 rounded-xl border-2 border-emerald-500/20 bg-gradient-to-br from-emerald-500/[0.06] to-transparent flex flex-col items-center justify-center text-center"
                    style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}>
                    <div className="text-[9px] text-emerald-400/50 font-bold tracking-widest mb-3">CEVAP</div>
                    <p className="text-sm text-foreground/80 leading-relaxed px-2">{back}</p>
                    {ref_text && <p className="text-[9px] text-muted-foreground/30 mt-auto pt-2">{ref_text}</p>}
                </div>
            </div>
        </div>
    );
};

// Güvenli inline markdown renderer: önce HTML escape, sonra regex ile
// yalnızca bold/italic/code dönüşümü. Böylece içerik kaynaklı XSS
// (örn. <img src=x onerror=alert(1)>) olamaz.
const escapeHtml = (s) => String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const renderMarkdownInline = (text) => {
    if (!text) return text;
    const safe = escapeHtml(text)
        .replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground font-semibold">$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/`(.*?)`/g, '<code class="text-purple-400 bg-purple-500/10 px-1 rounded text-xs">$1</code>');
    return <span dangerouslySetInnerHTML={{__html: safe}} />;
};

const SlideViewer = ({ slidesData }) => {
    const [current, setCurrent] = React.useState(0);
    let slides;
    try { slides = typeof slidesData === 'string' ? JSON.parse(slidesData) : slidesData; } catch { slides = null; }
    if (!slides || !Array.isArray(slides)) return <p className="text-sm text-muted-foreground p-5">Slayt yuklenemedi</p>;

    const slide = slides[current];
    return (
        <div>
            <div className="rounded-xl border border-border bg-gradient-to-br from-background to-muted/10 min-h-[240px] flex flex-col justify-start p-6 lg:p-8 relative overflow-hidden">
                <div className="absolute top-3 right-4 text-[10px] text-muted-foreground/30">{current + 1} / {slides.length}</div>
                <div className="absolute top-0 left-0 w-full h-1 bg-muted/20"><div className="h-full bg-purple-500/50 transition-all" style={{width: `${((current + 1) / slides.length) * 100}%`}} /></div>
                <h2 className="text-lg lg:text-xl font-bold text-foreground mb-4 mt-2 pr-12">{renderMarkdownInline(slide.title)}</h2>
                <ul className="space-y-2.5">
                    {(slide.bullets || []).map((b, i) => (
                        <li key={i} className="flex items-start gap-3">
                            <span className="w-1.5 h-1.5 rounded-full bg-purple-400 shrink-0 mt-2" />
                            <span className="text-sm lg:text-base text-foreground/75 leading-relaxed">{renderMarkdownInline(b)}</span>
                        </li>
                    ))}
                </ul>
                {/* slide.notes: eğitmene yönelik — öğrenciye gösterilmez */}
            </div>
            <div className="flex items-center justify-between mt-3">
                <Button variant="ghost" size="sm" disabled={current === 0} onClick={() => setCurrent(current - 1)}>
                    <ChevronLeft className="w-4 h-4 mr-1" />Önceki
                </Button>
                <div className="flex gap-1">
                    {slides.map((_, i) => (
                        <button key={i} onClick={() => setCurrent(i)}
                            className={`w-2 h-2 rounded-full transition-all ${i === current ? 'bg-purple-400 w-4' : 'bg-muted-foreground/20'}`} />
                    ))}
                </div>
                <Button variant="ghost" size="sm" disabled={current >= slides.length - 1} onClick={() => setCurrent(current + 1)}>
                    Sonraki<ChevronRight className="w-4 h-4 ml-1" />
                </Button>
            </div>
        </div>
    );
};

const LessonPage = () => {
    const navigate = useNavigate();
    const { lessonId } = useParams();
    const { user } = useAuth();
    const [lesson, setLesson] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [topic, setTopic] = useState(null);
    
    // Content and Quiz state
    const [currentView, setCurrentView] = useState('content'); // content, quiz, materials
    const [currentBlockIndex, setCurrentBlockIndex] = useState(0);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [showFeedback, setShowFeedback] = useState(false);
    const [answers, setAnswers] = useState({});
    const [quizCompleted, setQuizCompleted] = useState(false);
    
    // Regulation Modal
    const [regulationModalOpen, setRegulationModalOpen] = useState(false);
    const [selectedRegulation, setSelectedRegulation] = useState(null);
    
    // Progress tracking
    const startTimeRef = useRef(Date.now());
    const [savedProgress, setSavedProgress] = useState(null);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [blockTimes, setBlockTimes] = useState({});
    
    // Gamification
    const [xpEarned, setXpEarned] = useState(0);
    const [showConfetti, setShowConfetti] = useState(false);
    const [nextLesson, setNextLesson] = useState(null);

    // Tenant süre zorunluluğu oranı (0.0 = zorunluluk yok, 1.0 = tam süre)
    // Tenant admin ayarlayabilir; default %20
    const [minTimeRatio, setMinTimeRatio] = useState(0.20);
    useEffect(() => {
        fetch(`${API_URL}/api/tenant/exam-settings`, { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } })
            .then(r => r.ok ? r.json() : null)
            .then(d => {
                if (d?.settings) {
                    const enforced = d.settings.lesson_min_duration_enforced !== false;
                    const ratio = typeof d.settings.lesson_min_time_ratio === 'number' ? d.settings.lesson_min_time_ratio : 0.20;
                    setMinTimeRatio(enforced ? ratio : 0);
                }
            })
            .catch(() => {});
    }, []);

    // Test user check
    const isTestUser = user?.email?.includes('test') ||
                       user?.email?.includes('demo') ||
                       user?.email?.includes('admin') ||
                       user?.email?.includes('student') ||
                       user?.email?.includes('example') ||
                       user?.role === 'super_admin';

    // Time calculations — tenant ratio'ya göre (0 = zorunluluk yok, default %20)
    const minDurationSeconds = (lesson?.min_duration_minutes || 5) * 60;
    const requiredMinTime = Math.floor(minDurationSeconds * minTimeRatio);
    const hasMetMinTime = isTestUser || minTimeRatio === 0 || elapsedTime >= requiredMinTime;
    
    // Content blocks filtering
    const isBlockEmpty = (block) => {
        if (!block) return true;
        const hasContent = block.content && block.content.trim().length > 0;
        const hasItems = block.items && block.items.length > 0;
        const hasTitle = block.title && block.title.trim().length > 0;
        const hasUrl = block.url && block.url.trim().length > 0;
        
        switch (block.type) {
            case 'image':
            case 'video':
                return !hasUrl;
            case 'list':
            case 'summary':
                return !hasItems && !hasContent;
            case 'scenario':
            case 'example':
                return !hasContent;
            default:
                return !hasContent && !hasItems && !hasTitle;
        }
    };

    const rawContentBlocks = lesson?.content_blocks || [];
    const contentBlocks = rawContentBlocks.filter(block => !isBlockEmpty(block));
    const hasContent = contentBlocks.length > 0;
    const hasQuestions = questions.length > 0;
    const hasMaterials = lesson?.has_materials && lesson?.materials;
    
    const totalBlocks = contentBlocks.length || 1;
    const minTimePerBlock = Math.max(3, Math.floor(minDurationSeconds / (totalBlocks * 5)));
    const currentBlockTime = blockTimes[currentBlockIndex] || 0;
    const hasMetBlockMinTime = isTestUser || currentBlockTime >= minTimePerBlock;

    // Timer effect
    useEffect(() => {
        const timer = setInterval(() => {
            setElapsedTime(prev => prev + 1);
            setBlockTimes(prev => ({
                ...prev,
                [currentBlockIndex]: (prev[currentBlockIndex] || 0) + 1
            }));
        }, 1000);
        return () => clearInterval(timer);
    }, [currentBlockIndex]);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lessonId]);

    const loadData = async () => {
        try {
            setLoading(true);
            // Tüm state'leri sıfırla (yeni ders için temiz başlangıç)
            setLesson(null);
            setQuestions([]);
            setTopic(null);
            setCurrentView('content');
            setCurrentBlockIndex(0);
            setCurrentQuestionIndex(0);
            setSelectedAnswer(null);
            setShowFeedback(false);
            setAnswers({});
            setQuizCompleted(false);
            setXpEarned(0);
            setShowConfetti(false);
            setNextLesson(null);
            setSavedProgress(null);
            startTimeRef.current = Date.now();
            setElapsedTime(0);
            setBlockTimes({});
            
            const [lessonRes, questionsRes, progressRes] = await Promise.all([
                lessonsAPI.getById(lessonId),
                lessonsAPI.getQuestions(lessonId),
                progressAPI.getLesson(lessonId)
            ]);
            setLesson(lessonRes.data);
            setQuestions(questionsRes.data);
            setSavedProgress(progressRes.data);

            // Not pre-populating `answers` from saved quiz_answers: doing so
            // would mix prior attempts into the current session. calculateResults
            // already falls back to savedProgress for the completion card when
            // answers is empty (i.e. the user hasn't started a new attempt yet).
            
            if (lessonRes.data?.topic_id) {
                topicsAPI.getById(lessonRes.data.topic_id).then(topicRes => {
                    setTopic(topicRes.data);
                }).catch(() => {});
            }
            
            gamificationAPI.getNextLesson(lessonId).then(res => {
                if (res.data?.has_next) {
                    setNextLesson(res.data);
                }
            }).catch(() => {});
            
            if (progressRes.data?.content_progress > 0) {
                setCurrentBlockIndex(Math.min(progressRes.data.content_progress, (lessonRes.data.content_blocks?.length || 1) - 1));
            }
            if (progressRes.data?.quiz_completed) {
                // Lesson already completed - start fresh for review
                setCurrentView('content');
                setCurrentBlockIndex(0);
            } else if (progressRes.data?.content_completed && lessonRes.data.question_count > 0) {
                setCurrentView('quiz');
            } else if (progressRes.data?.content_completed && lessonRes.data.question_count === 0) {
                const lastBlockIndex = (lessonRes.data.content_blocks?.length || 1) - 1;
                setCurrentBlockIndex(lastBlockIndex);
            }
        } catch (err) {
            toast.error('Ders yüklenirken hata oluştu');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };
    
    const saveContentProgress = async (blockIndex) => {
        try {
            const timeSpent = Math.floor((Date.now() - startTimeRef.current) / 1000);
            startTimeRef.current = Date.now();
            await progressAPI.updateContent(lessonId, {
                block_index: blockIndex,
                total_blocks: contentBlocks.length,
                time_spent_seconds: timeSpent
            });
        } catch (err) {
            console.error('Progress save error:', err);
        }
    };
    
    const saveQuizProgress = async (completed, score, total, answersList) => {
        try {
            await progressAPI.updateQuiz(lessonId, {
                started: true,
                completed,
                score,
                total,
                answers: answersList
            });
        } catch (err) {
            console.error('Quiz progress save error:', err);
        }
    };

    useEffect(() => {
        if (!loading && !hasContent && hasQuestions && currentView === 'content') {
            setCurrentView('quiz');
        }
    }, [loading, hasContent, hasQuestions, currentView]);

    const nextBlock = () => {
        if (currentBlockIndex < contentBlocks.length - 1) {
            const newIndex = currentBlockIndex + 1;
            setCurrentBlockIndex(newIndex);
            saveContentProgress(newIndex);
        } else if (hasQuestions) {
            saveContentProgress(contentBlocks.length - 1);
            setCurrentView('quiz');
        }
    };
    
    const handleCompleteLessonWithoutQuiz = async () => {
        try {
            await progressAPI.updateQuiz(lessonId, {
                started: true,
                completed: true,
                score: 0,
                total: 0,
                answers: []
            });
            
            const earnedXP = 50;
            setXpEarned(earnedXP);
            setQuizCompleted(true);
            setShowConfetti(true);
            setTimeout(() => setShowConfetti(false), 5000);
            toast.success(`Ders tamamlandı! +${earnedXP} XP kazandınız`);
        } catch (err) {
            console.error('Lesson complete error:', err);
            toast.error('Ders tamamlanamadı');
        }
    };

    const prevBlock = () => {
        if (currentBlockIndex > 0) {
            setCurrentBlockIndex(prev => prev - 1);
        }
    };

    // Quiz logic
    const currentQuestion = questions[currentQuestionIndex];
    

    const handleAnswerSelect = (index) => {
        if (showFeedback) return;
        setSelectedAnswer(index);
    };

    const handleSubmitAnswer = () => {
        if (selectedAnswer === null) {
            toast.error('Lütfen bir cevap seçin');
            return;
        }
        
        const options = currentQuestion.options || [];
        let isCorrect = false;
        
        if (options.length > 0) {
            const selectedOption = options[selectedAnswer];
            if (typeof selectedOption === 'object' && selectedOption.is_correct !== undefined) {
                isCorrect = selectedOption.is_correct;
            } else {
                isCorrect = selectedAnswer === currentQuestion.correct_answer;
            }
        }
        
        setAnswers(prev => ({
            ...prev,
            [currentQuestion.id]: { selected: selectedAnswer, correct: isCorrect }
        }));
        setShowFeedback(true);
    };

    const handleNextQuestion = () => {
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(prev => prev + 1);
            setSelectedAnswer(null);
            setShowFeedback(false);
        } else {
            const total = questions.length;
            const correct = Object.values(answers).filter(a => a.correct).length;
            const answersList = Object.entries(answers).map(([qId, ans]) => ({
                question_id: qId,
                selected_option: ans.selected,
                is_correct: ans.correct
            }));
            saveQuizProgress(true, correct, total, answersList);
            setQuizCompleted(true);
            
            const passPercentage = (correct / total) * 100;
            const passed = passPercentage >= 80;
            let earnedXP = 100;
            if (passPercentage === 100) {
                earnedXP = 200;
            } else if (passPercentage >= 90) {
                earnedXP = 150;
            }
            
            if (passed) {
                setXpEarned(earnedXP);
                setShowConfetti(true);
                setTimeout(() => setShowConfetti(false), 5000);
                gamificationAPI.getNextLesson(lessonId).then(res => {
                    if (res.data?.has_next) {
                        setNextLesson(res.data);
                    }
                }).catch(() => {});
            }
            
            const wrongRegulations = questions
                .filter(q => !answers[q.id]?.correct && q.regulation_reference)
                .map(q => q.regulation_reference)
                .filter((ref, idx, arr) => {
                    const key = `${ref.law_id}-${ref.article}`;
                    return arr.findIndex(r => `${r.law_id}-${r.article}` === key) === idx;
                });
            
            if (wrongRegulations.length > 0) {
                weakTopicsAPI.save({
                    wrong_regulations: wrongRegulations,
                    lesson_id: lessonId
                }).catch(err => console.error('Weak topics save error:', err));
            }
            
            toast.success(passed ? `Tebrikler! +${earnedXP} XP kazandın!` : 'Quiz tamamlandı!');
        }
    };

    const calculateResults = () => {
        const answersTotal = Object.keys(answers).length;
        if (answersTotal > 0) {
            const correct = Object.values(answers).filter(a => a.correct).length;
            return { total: answersTotal, correct, percentage: Math.round((correct / answersTotal) * 100) };
        }
        
        if (savedProgress?.quiz_completed) {
            const total = savedProgress.quiz_total || questions.length || 0;
            const correct = savedProgress.quiz_score || 0;
            const percentage = total > 0 ? Math.round((correct / total) * 100) : (savedProgress.quiz_passed ? 100 : 0);
            return { total, correct, percentage };
        }
        
        return { total: 0, correct: 0, percentage: 0 };
    };

    const results = calculateResults();
    const levelColor = LEVEL_COLORS[lesson?.difficulty_level] || LEVEL_COLORS.L1;

    const handleRetry = () => {
        setCurrentQuestionIndex(0);
        setSelectedAnswer(null);
        setShowFeedback(false);
        setAnswers({});
        setQuizCompleted(false);
    };

    const handleOpenOriginalDoc = async () => {
        try {
            const token = localStorage.getItem('auth_token');
            const res = await fetch(
                `${API_URL}/api/internal-docs/serve-by-lesson/${lesson.id}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!res.ok) { toast.error('Orijinal dosya bulunamadı'); return; }
            const blob = await res.blob();
            window.open(URL.createObjectURL(blob), '_blank');
        } catch {
            toast.error('Dosya açılamadı');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <p className="text-muted-foreground">Yükleniyor...</p>
                </div>
            </div>
        );
    }

    if (!lesson) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-xl font-semibold mb-2">Ders bulunamadı</h2>
                    <Button onClick={() => navigate('/dashboard')}>
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Dashboard'a Dön
                    </Button>
                </div>
            </div>
        );
    }

    const breadcrumbs = lesson ? [
        { label: topic?.module_name || 'Modül', href: `/module/${topic?.module_id || lesson?.topic_id?.split('-')[0]?.replace('T', 'M')}` },
        { label: topic?.name || 'Konu', href: `/topic/${lesson.topic_id}` },
        { label: `${lesson.difficulty_level} ${lesson.name?.substring(0, 15)}${lesson.name?.length > 15 ? '...' : ''}` }
    ] : [];

    return (
        <div className="min-h-screen bg-background">
            <StudentHeader breadcrumbs={breadcrumbs} />
            
            {/* Progress Bar */}
            <div className="sticky top-14 z-40 bg-background/80 backdrop-blur-md border-b border-border/50">
                <div className="max-w-[1200px] mx-auto px-4">
                    <div className="flex items-center justify-between py-2">
                        <div className="flex items-center gap-2">
                            <Badge className={`bg-gradient-to-r ${levelColor} text-foreground`}>
                                {lesson.difficulty_level}
                            </Badge>
                            <span className="text-sm font-medium truncate max-w-[200px]">{lesson.name}</span>
                        </div>
                        <div className="flex items-center gap-3">
                            {currentView === 'content' && hasContent && (
                                <span className="text-xs text-muted-foreground">
                                    {currentBlockIndex + 1} / {contentBlocks.length}
                                </span>
                            )}
                            {currentView === 'quiz' && hasQuestions && (
                                <span className="text-xs text-muted-foreground">
                                    Soru {currentQuestionIndex + 1} / {questions.length}
                                </span>
                            )}
                        </div>
                    </div>
                    <Progress 
                        value={
                            currentView === 'content' 
                                ? ((currentBlockIndex + 1) / Math.max(contentBlocks.length, 1)) * (hasQuestions ? 50 : 100)
                                : 50 + ((currentQuestionIndex + 1) / Math.max(questions.length, 1)) * 50
                        } 
                        className="h-1 -mb-px"
                    />
                </div>
            </div>

            <main className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Tab Navigation */}
                <div className="flex items-center gap-1.5 mb-6 overflow-x-auto pb-1 scrollbar-hide" data-testid="lesson-tabs">
                    <button onClick={() => setCurrentView('content')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${currentView === 'content' || currentView === 'quiz' ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-muted/30 text-muted-foreground hover:text-foreground border border-border/50'}`}
                        data-testid="tab-content">
                        <BookOpen className="w-3 h-3 inline mr-1" />Ders
                    </button>
                    {lesson?.materials?.report_content && (
                        <button onClick={() => setCurrentView('mat_report')}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${currentView === 'mat_report' ? 'bg-blue-500/20 text-blue-400 shadow-sm' : 'bg-muted/30 text-muted-foreground hover:text-foreground border border-border/50'}`}>
                            Rapor
                        </button>
                    )}
                    {lesson?.materials?.flashcards?.length > 0 && (
                        <button onClick={() => setCurrentView('mat_flashcards')}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${currentView === 'mat_flashcards' ? 'bg-amber-500/20 text-amber-400 shadow-sm' : 'bg-muted/30 text-muted-foreground hover:text-foreground border border-border/50'}`}>
                            Kartlar
                        </button>
                    )}
                    {(lesson?.materials?.audio_url || lesson?.materials?.video_url) && (
                        <button onClick={() => setCurrentView('mat_podcast')}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${currentView === 'mat_podcast' ? 'bg-red-500/20 text-red-400 shadow-sm' : 'bg-muted/30 text-muted-foreground hover:text-foreground border border-border/50'}`}>
                            Podcast
                        </button>
                    )}
                    {lesson?.materials?.slides_url && (
                        <button onClick={() => setCurrentView('mat_slides')}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${currentView === 'mat_slides' ? 'bg-purple-500/20 text-purple-400 shadow-sm' : 'bg-muted/30 text-muted-foreground hover:text-foreground border border-border/50'}`}>
                            Slayt
                        </button>
                    )}
                    {lesson?.glossary?.length > 0 && (
                        <button onClick={() => setCurrentView('glossary')}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${currentView === 'glossary' ? 'bg-cyan-500/20 text-cyan-400 shadow-sm' : 'bg-muted/30 text-muted-foreground hover:text-foreground border border-border/50'}`}>
                            Sozluk
                        </button>
                    )}
                    {lesson?.red_flags?.length > 0 && (
                        <button onClick={() => setCurrentView('red_flags')}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${currentView === 'red_flags' ? 'bg-red-500/20 text-red-400 shadow-sm' : 'bg-muted/30 text-muted-foreground hover:text-foreground border border-border/50'}`}>
                            Dikkat
                        </button>
                    )}
                    {lesson?.key_takeaways?.length > 0 && (
                        <button onClick={() => setCurrentView('takeaways')}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${currentView === 'takeaways' ? 'bg-emerald-500/20 text-emerald-400 shadow-sm' : 'bg-muted/30 text-muted-foreground hover:text-foreground border border-border/50'}`}>
                            Onemli
                        </button>
                    )}
                </div>

                {/* Report - Markdown Rendered */}
                {currentView === 'mat_report' && lesson?.materials?.report_content && (
                    <Card><CardContent className="p-6 max-h-[60vh] overflow-y-auto">
                        <div className="space-y-3">
                            {lesson.materials.report_content.split('\n').map((line, i) => {
                                const t = line.trim();
                                if (!t) return <div key={i} className="h-2" />;
                                if (t.startsWith('# ')) return <h2 key={i} className="text-lg font-bold text-foreground mt-4 mb-2">{t.slice(2)}</h2>;
                                if (t.startsWith('## ')) return <h3 key={i} className="text-base font-bold text-foreground/90 mt-4 mb-1.5">{t.slice(3)}</h3>;
                                if (t.startsWith('### ')) return <h4 key={i} className="text-sm font-bold text-foreground/80 mt-3 mb-1">{t.slice(4)}</h4>;
                                if (t.startsWith('- ') || t.startsWith('* ')) return <li key={i} className="text-sm text-foreground/75 leading-7 ml-4 list-disc">{t.slice(2).replace(/\*\*(.*?)\*\*/g, '$1')}</li>;
                                return <p key={i} className="text-sm text-foreground/75 leading-7" dangerouslySetInnerHTML={{__html: escapeHtml(t).replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground">$1</strong>')}} />;
                            })}
                        </div>
                    </CardContent></Card>
                )}

                {/* Flashcards - all cards grid with flip */}
                {currentView === 'mat_flashcards' && lesson?.materials?.flashcards && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {lesson.materials.flashcards.map((card, i) => (
                            <FlipCard key={i} front={card.front} back={card.back} ref_text={card.ref} />
                        ))}
                    </div>
                )}

                {/* Podcast - audio player + text script */}
                {currentView === 'mat_podcast' && (lesson?.materials?.audio_url || lesson?.materials?.video_url) && (
                    <Card><CardContent className="p-5 max-h-[60vh] overflow-y-auto">
                        {/* Audio player if file uploaded */}
                        {lesson.materials.video_url && (
                            <div className="mb-5 p-4 rounded-xl bg-red-500/5 border border-red-500/10">
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="w-8 h-8 rounded-lg bg-red-500/15 flex items-center justify-center">
                                        <Play className="w-4 h-4 text-red-400" />
                                    </div>
                                    <span className="text-sm font-medium">Sesli Ozet</span>
                                </div>
                                <audio controls className="w-full" data-testid="podcast-player">
                                    <source src={`${process.env.REACT_APP_BACKEND_URL}${lesson.materials.video_url}`} />
                                </audio>
                            </div>
                        )}
                        {/* YouTube embed */}
                        {!lesson.materials.video_url && lesson.materials.audio_url?.startsWith('http') && (
                            lesson.materials.audio_url.includes('youtube') || lesson.materials.audio_url.includes('youtu.be') ? (
                                <iframe src={lesson.materials.audio_url.replace('watch?v=', 'embed/')} className="w-full h-[360px] rounded-lg mb-4" allowFullScreen />
                            ) : (
                                <audio controls className="w-full mb-4"><source src={lesson.materials.audio_url} /></audio>
                            )
                        )}
                        {/* Text script */}
                        {lesson.materials.audio_url && !lesson.materials.audio_url.startsWith('http') && (
                            <div className="space-y-2">
                                <div className="text-[10px] text-muted-foreground/40 font-medium uppercase tracking-wider mb-3">Podcast Senaryosu</div>
                                {lesson.materials.audio_url.split('\n').map((line, i) => {
                                    const t = line.trim();
                                    if (!t) return <div key={i} className="h-1" />;
                                    const clean = t.replace(/\*+/g, '');
                                    if (/^(A|Z|Ahmet|Zeynep)\s*:/.test(clean)) {
                                        const match = clean.match(/^(A|Z|Ahmet|Zeynep)\s*:\s*(.*)/);
                                        const isA = match[1] === 'A' || match[1] === 'Ahmet';
                                        return <p key={i} className="text-sm leading-7"><span className={`font-bold ${isA ? 'text-blue-400' : 'text-pink-400'}`}>{isA ? 'Ahmet' : 'Zeynep'}:</span> <span className="text-foreground/75">{match[2]}</span></p>;
                                    }
                                    if (t.startsWith('#') || (t === t.toUpperCase() && t.length < 50)) return <h4 key={i} className="text-xs font-bold text-foreground/50 mt-4 mb-1 uppercase tracking-wide">{clean.replace(/^#+\s*/, '')}</h4>;
                                    return <p key={i} className="text-sm text-foreground/60 leading-7">{clean}</p>;
                                })}
                            </div>
                        )}
                    </CardContent></Card>
                )}

                {/* Slides - presentation style */}
                {currentView === 'mat_slides' && lesson?.materials?.slides_url && (
                    <SlideViewer slidesData={lesson.materials.slides_url} />
                )}

                {/* Glossary */}
                {currentView === 'glossary' && lesson?.glossary && (
                    <Card><CardContent className="p-5 max-h-[60vh] overflow-y-auto">
                        <div className="space-y-3">{lesson.glossary.map((item, i) => (
                            <div key={i} className="p-3 rounded-lg bg-muted/20 border border-border/30">
                                <div className="flex items-baseline gap-2">
                                    <span className="text-sm font-bold text-cyan-400">{typeof item === 'string' ? item : (item.term || item.term_tr)}</span>
                                    {item.english && <span className="text-[10px] text-muted-foreground">({item.english})</span>}
                                </div>
                                {item.definition && <p className="text-sm text-foreground/70 mt-1 leading-relaxed">{item.definition}</p>}
                            </div>
                        ))}</div>
                    </CardContent></Card>
                )}

                {/* Red Flags */}
                {currentView === 'red_flags' && lesson?.red_flags && (
                    <Card><CardContent className="p-5 max-h-[60vh] overflow-y-auto">
                        <div className="space-y-2">{lesson.red_flags.map((item, i) => (
                            <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-red-500/5 border border-red-500/10">
                                <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                                <p className="text-sm text-foreground/75 leading-relaxed">{typeof item === 'string' ? item : (item.text || item.description || JSON.stringify(item))}</p>
                            </div>
                        ))}</div>
                    </CardContent></Card>
                )}

                {/* Key Takeaways */}
                {currentView === 'takeaways' && lesson?.key_takeaways && (
                    <Card><CardContent className="p-5 max-h-[60vh] overflow-y-auto">
                        <div className="space-y-2">{lesson.key_takeaways.map((item, i) => (
                            <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/10">
                                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                                <p className="text-sm text-foreground/75 leading-relaxed">{typeof item === 'string' ? item : (item.text || item.description || JSON.stringify(item))}</p>
                            </div>
                        ))}</div>
                    </CardContent></Card>
                )}

                {currentView !== 'summary' && !currentView.startsWith('mat_') && !['glossary','red_flags','takeaways'].includes(currentView) && (<>
                {/* No content view */}
                {currentView === 'content' && !hasContent && !hasQuestions && !quizCompleted && (
                    <div className="space-y-6">
                        <Card className="min-h-[400px]">
                            <CardContent className="p-8 flex flex-col items-center justify-center h-full">
                                <BookOpen className="w-16 h-16 text-muted-foreground mb-4" />
                                <h3 className="text-lg font-medium mb-2">Bu ders henüz içerik eklenmemiş</h3>
                                <p className="text-sm text-muted-foreground mb-6 text-center">
                                    İçerik hazırlandığında burada görüntülenecek.
                                </p>
                                <Button 
                                    onClick={handleCompleteLessonWithoutQuiz}
                                    className="bg-emerald-600 hover:bg-emerald-700"
                                >
                                    Dersi Tamamla <CheckCircle className="w-4 h-4 ml-2" />
                                </Button>
                            </CardContent>
                        </Card>
                    </div>
                )}
                
                {/* Content View */}
                {currentView === 'content' && hasContent && (
                    <div className="space-y-6">
                        <Card className="min-h-[400px]">
                            <CardContent className={(contentBlocks[currentBlockIndex]?.content || '').length > 1500 ? 'p-6 sm:p-10' : 'p-8'}>
                                <ContentBlockRenderer block={contentBlocks[currentBlockIndex]} />
                            </CardContent>
                        </Card>
                        
                        {lesson?.source_type === 'internal_policy' && (
                            <div className="flex justify-end">
                                <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground" onClick={handleOpenOriginalDoc}>
                                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                                    Orijinal Belgeyi Görüntüle
                                </Button>
                            </div>
                        )}

                        <div className="flex items-center justify-between">
                            <Button
                                variant="outline"
                                onClick={prevBlock}
                                disabled={currentBlockIndex === 0}
                            >
                                <ChevronLeft className="w-4 h-4 mr-2" />
                                Önceki
                            </Button>
                            {currentBlockIndex === contentBlocks.length - 1 ? (
                                hasQuestions ? (
                                    <>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                                            <Clock className="w-3 h-3" />
                                            {isTestUser 
                                                ? `Kalan: ${Math.floor(elapsedTime/60) > 0 ? Math.floor(elapsedTime/60) + 'dk ' : ''}${elapsedTime%60}sn` 
                                                : hasMetMinTime 
                                                    ? '✓ Süre tamam' 
                                                    : `${Math.max(0, Math.ceil((requiredMinTime - elapsedTime) / 60))}dk kaldı`}
                                        </span>
                                        <Button 
                                            onClick={nextBlock}
                                            disabled={!hasMetMinTime}
                                        >
                                            Quiz'e Geç <Scale className="w-4 h-4 ml-2" />
                                        </Button>
                                    </div>
                                    </>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                                            <Clock className="w-3 h-3" />
                                            {isTestUser 
                                                ? `Kalan: ${Math.floor(elapsedTime/60) > 0 ? Math.floor(elapsedTime/60) + 'dk ' : ''}${elapsedTime%60}sn` 
                                                : hasMetMinTime 
                                                    ? '✓ Süre tamam' 
                                                    : `${Math.max(0, Math.ceil((requiredMinTime - elapsedTime) / 60))}dk kaldı`}
                                        </span>
                                        <Button 
                                            onClick={handleCompleteLessonWithoutQuiz}
                                            disabled={!hasMetMinTime}
                                            className="bg-emerald-600 hover:bg-emerald-700"
                                        >
                                            Dersi Tamamla <CheckCircle className="w-4 h-4 ml-2" />
                                        </Button>
                                    </div>
                                )
                            ) : (
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                                        <Clock className="w-3 h-3" />
                                        {isTestUser 
                                            ? `Kalan: ${currentBlockTime}sn` 
                                            : hasMetBlockMinTime 
                                                ? '✓' 
                                                : `${Math.max(0, minTimePerBlock - currentBlockTime)}sn`}
                                    </span>
                                    <Button 
                                        onClick={nextBlock}
                                        disabled={!hasMetBlockMinTime}
                                    >
                                        Sonraki <ChevronRight className="w-4 h-4 ml-2" />
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Quiz View */}
                {currentView === 'quiz' && hasQuestions && !quizCompleted && currentQuestion && (
                    <QuizView 
                        currentQuestion={currentQuestion}
                        currentQuestionIndex={currentQuestionIndex}
                        questionsLength={questions.length}
                        selectedAnswer={selectedAnswer}
                        showFeedback={showFeedback}
                        onAnswerSelect={handleAnswerSelect}
                        onSubmitAnswer={handleSubmitAnswer}
                        onNextQuestion={handleNextQuestion}
                    />
                )}

                {/* Quiz Results */}
                {currentView === 'quiz' && quizCompleted && (
                    <QuizResults 
                        lesson={lesson}
                        topic={topic}
                        questions={questions}
                        answers={answers}
                        results={results}
                        xpEarned={xpEarned}
                        showConfetti={showConfetti}
                        nextLesson={nextLesson}
                        hasQuestions={hasQuestions}
                        onRetry={handleRetry}
                        regulationModalOpen={regulationModalOpen}
                        selectedRegulation={selectedRegulation}
                        setRegulationModalOpen={setRegulationModalOpen}
                        setSelectedRegulation={setSelectedRegulation}
                    />
                )}
                </>)}
            </main>
        </div>
    );
};

export default LessonPage;
